import type { StateCreator } from 'zustand';
import type { CompositionPlan } from '@/types/marketplaceComposition.types';
import type { SearchResult } from '@/types/searchResponse.types';
import {
  mapPlanStepsToSelection,
  type StepMetadata,
} from '@/adapters/compositionPlanToSelection.adapter';

// ---------------------------------------------------------------------------
// Slice interface
// ---------------------------------------------------------------------------

export interface SearchSelectionSlice {
  // State
  selectedItemIds: string[];
  stepMeta: Record<string, StepMetadata>;

  // Actions
  toggleSelection: (itemId: string) => void;
  select: (itemId: string) => void;
  deselect: (itemId: string) => void;
  preseedFromPlan: (plan: CompositionPlan, results: SearchResult[]) => void;
  clearSelection: () => void;
  resetSelection: () => void;
  isSelected: (itemId: string) => boolean;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialSearchSelectionState: Omit<
  SearchSelectionSlice,
  | 'toggleSelection'
  | 'select'
  | 'deselect'
  | 'preseedFromPlan'
  | 'clearSelection'
  | 'resetSelection'
  | 'isSelected'
> = {
  selectedItemIds: [],
  stepMeta: {},
};

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

export const createSearchSelectionSlice: StateCreator<
  SearchSelectionSlice,
  [],
  [],
  SearchSelectionSlice
> = (set, get) => ({
  ...initialSearchSelectionState,

  toggleSelection: (itemId: string) => {
    const { selectedItemIds } = get();
    set({
      selectedItemIds: selectedItemIds.includes(itemId)
        ? selectedItemIds.filter((id) => id !== itemId)
        : [...selectedItemIds, itemId],
    });
  },

  select: (itemId: string) => {
    const { selectedItemIds } = get();
    if (!selectedItemIds.includes(itemId)) {
      set({ selectedItemIds: [...selectedItemIds, itemId] });
    }
  },

  deselect: (itemId: string) => {
    const { selectedItemIds } = get();
    set({
      selectedItemIds: selectedItemIds.filter((id) => id !== itemId),
    });
  },

  preseedFromPlan: (plan: CompositionPlan, results: SearchResult[]) => {
    const { itemIds, stepMeta } = mapPlanStepsToSelection(plan, results);
    set({
      selectedItemIds: itemIds,
      stepMeta,
    });
  },

  clearSelection: () => {
    set({ selectedItemIds: [], stepMeta: {} });
  },

  resetSelection: () => {
    set({ ...initialSearchSelectionState });
  },

  isSelected: (itemId: string) => {
    const { selectedItemIds } = get();
    return selectedItemIds.includes(itemId);
  },
});
